/**
 * Projects Service API
 * Project management and employee assignment endpoints
 */

import { createAPIClient } from '../client'
import { API_CONFIG } from '../config'

const client = createAPIClient(API_CONFIG.PROJECTS.baseURL, API_CONFIG.PROJECTS.timeout)

export const projectsService = {
  // ==================== Client document library (staff side) ====================
  //
  // Authenticated, unlike the public library the CLIENT opens. Built on the shared client so the
  // bearer token is attached by the interceptor — the first cut used a bare `fetch()` and 401'd,
  // because nothing in a raw fetch carries the session.

  /** Whether this client has a library link, and what it is. Does not mint one. */
  getClientDocumentLibrary: async (clientId) => {
    const response = await client.get(`/clients/${clientId}/document-library`)
    return response.data
  },

  /** Issue the link (or un-revoke it); optionally email it. Idempotent — same url each time. */
  issueClientDocumentLibrary: async (clientId, email = null) => {
    const response = await client.post(`/clients/${clientId}/document-library`,
                                       email ? { email } : {})
    return response.data
  },

  /** Kill the link. The documents are untouched; only the way in goes away. */
  revokeClientDocumentLibrary: async (clientId) => {
    const response = await client.delete(`/clients/${clientId}/document-library`)
    return response.data
  },

  // ==================== Completion dates on finished jobs ====================
  //
  // The backlog of `completed` jobs with no completion date. They are invisible to any date-ranged
  // report because that is the column a period selects on. See the backend service for why these
  // are suggestions somebody accepts rather than a backfill somebody runs.

  /** The backlog, each row with a suggested date and the basis for it. */
  getProjectsNeedingCompletionDate: async () => {
    const response = await client.get('/projects/needs-completion-date')
    return response.data
  },

  /** The same backlog as an .xlsx. A blob through the authed client, not a plain <a href> —
   *  nothing in a bare link carries the bearer token, so the download would 401. */
  downloadCompletionDateWorksheet: async () => {
    const response = await client.get('/projects/needs-completion-date.xlsx',
                                      { responseType: 'blob' })
    return response.data
  },

  /** Save dates typed on screen. `dryRun` reports what would happen and writes nothing. */
  setCompletionDates: async (entries, { dryRun = false, overwrite = false } = {}) => {
    const response = await client.post('/projects/completion-dates',
                                       { entries, dry_run: dryRun, overwrite })
    return response.data
  },

  /** Read a filled-in worksheet back. Same validation as the on-screen path — one endpoint's
   *  rules, not two that can drift. */
  importCompletionDates: async (file, { dryRun = false, overwrite = false } = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await client.post(
      `/projects/completion-dates/import?dry_run=${dryRun}&overwrite=${overwrite}`,
      formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    return response.data
  },

  // ==================== Projects ====================

  /**
   * Get all projects with optional filters
   * @param {object} params - Query parameters (status, search, skip, limit)
   * @returns {Promise} Projects array with pagination
   */
  getProjects: async (params = {}) => {
    const response = await client.get('/projects/', { params })
    return response.data
  },

  /**
   * Get project statistics for the dashboard
   * @returns {Promise} Statistics object { total_projects, active_projects }
   */
  getStatistics: async () => {
    const response = await client.get('/projects/statistics')
    return response.data
  },

  /**
   * Get project summaries (lightweight for dropdowns)
   * @returns {Promise} Array of project summaries
   */
  getProjectSummaries: async (params = {}) => {
    const response = await client.get('/projects/summary', { params })
    return response.data
  },

  /**
   * Get project by ID
   * @param {number} id - Project ID
   * @returns {Promise} Project data
   */
  getProject: async (id) => {
    const response = await client.get(`/projects/${id}`)
    return response.data
  },

  /**
   * A Capex job's figures for its project page - budget, contract, the customer's signature (C2 item 5). Needs
   * view_financial. `capex_job` is null for a project no Capex job tracks.
   */
  getCapexSummary: async (id) => {
    const response = await client.get(`/projects/${id}/capex-summary`)
    return response.data
  },

  /**
   * Every status move a project may make and the permission each needs - the rules every door applies
   * (`shared/project_status.py`). The edit form offers only these (C1 review round 2).
   * @returns {Promise<{moves: {from: string, to: string, permission: string}[]}>}
   */
  getStatusMoves: async () => {
    const response = await client.get('/projects/status-moves')
    return response.data
  },

  /**
   * Get project by project number
   * @param {string} projectNumber - Project number
   * @returns {Promise} Project data
   */
  getProjectByNumber: async (projectNumber) => {
    const response = await client.get(`/projects/number/${projectNumber}`)
    return response.data
  },

  /**
   * Get site projects (children) linked to a parent project
   * @param {string} projectId - Parent project ID
   * @returns {Promise} Array of site project summaries
   */
  getProjectSites: async (projectId) => {
    const response = await client.get(`/projects/${projectId}/sites`)
    return response.data
  },

  /**
   * Create new project
   * @param {object} projectData - Project data
   * @returns {Promise} Created project
   */
  createProject: async (projectData) => {
    const response = await client.post('/projects/', projectData)
    return response.data
  },

  /**
   * Update project
   * @param {number} id - Project ID
   * @param {object} projectData - Updated project data
   * @returns {Promise} Updated project
   */
  updateProject: async (id, projectData) => {
    const response = await client.put(`/projects/${id}`, projectData)
    return response.data
  },

  /**
   * Update project status (role-based transition)
   * @param {string} id - Project ID
   * @param {string} newStatus - New status value
   * @returns {Promise} Updated project
   */
  updateProjectStatus: async (id, newStatus) => {
    const response = await client.post(`/projects/${id}/status`, { status: newStatus })
    return response.data
  },

  saveLaborActuals: async (id, laborActuals) => {
    const response = await client.patch(`/projects/${id}/labor-actuals`, { labor_actuals: laborActuals })
    return response.data
  },

  saveSupervisionActuals: async (id, supervisionActuals) => {
    const response = await client.patch(`/projects/${id}/supervision-actuals`, { supervision_actuals: supervisionActuals })
    return response.data
  },

  /**
   * Delete project
   * @param {number} id - Project ID
   * @returns {Promise}
   */
  deleteProject: async (id) => {
    const response = await client.delete(`/projects/${id}`)
    return response.data
  },

  // getProjectBudgetAnalysis was removed with the /budget-analysis endpoint: it was a SECOND
  // "actual cost" engine that re-derived project costs from field tickets and disagreed with the
  // finance ledger. Project actuals come from the ledger — projectFinancialsAPI.summary() for
  // totals, .laborBreakdown() / .equipmentBreakdown() for detail.

  /**
   * Get budget line items for a project
   */
  getBudgetLines: async (id) => {
    const response = await client.get(`/projects/${id}/budget-lines`)
    return response.data
  },

  /**
   * Create budget line items for a project (bulk)
   */
  createBudgetLines: async (id, lines) => {
    const response = await client.post(`/projects/${id}/budget-lines`, lines)
    return response.data
  },

  /**
   * Delete budget line items for a project — ALL of them, or only one category.
   *
   * PASS THE CATEGORY IF YOU ONLY OWN ONE. A budget holds labor, supervision and equipment lines,
   * and a screen that loaded only one of those and then called this without a filter deleted the
   * other two and put nothing back. That is what ProjectForm did to every project converted from a
   * won bid, whose equipment lines are created by OpportunityDetail.
   */
  deleteBudgetLines: async (id, category) => {
    const response = await client.delete(`/projects/${id}/budget-lines`, {
      params: category ? { category } : undefined,
    })
    return response.data
  },

  /**
   * Get labor rate classifications
   */
  getLaborRates: async () => {
    const response = await client.get('/projects/labor-rates/classifications')
    return response.data
  },

  // ==================== Assignments ====================

  /**
   * Get active assignments with filters
   * @param {object} params - Query parameters (employee_id, project_id, role)
   * @returns {Promise} Array of active assignments
   */
  getActiveAssignments: async (params = {}) => {
    const response = await client.get('/assignments/active', { params })
    return response.data
  },

  /**
   * Get assignment history with temporal queries
   * @param {object} params - Query parameters (as_of_date, start_date, end_date)
   * @returns {Promise} Array of assignments
   */
  getAssignmentHistory: async (params = {}) => {
    const response = await client.get('/assignments/history', { params })
    return response.data
  },

  /**
   * Get employee's complete assignment history
   *
   * A STRING, and it always was: employee ids are the 36-char hr_employees UUIDs. This said
   * `{number}` and so did the backend's response model, which is why the route answered 500 to
   * every real id until 2026-09-07 (see EmployeeProjectHistory in project_schema.py). Nothing
   * calls this yet; the note is here so the next caller does not pass an integer.
   *
   * The payload carries `withheld_fields` on each assignment: a null `hourly_rate_override`
   * whose name is in that list is HIDDEN, not unset. Use shared/utils/withheld.js to tell them
   * apart rather than rendering a blank.
   *
   * @param {string} employeeId - Employee UUID
   * @returns {Promise} Employee with all assignments
   */
  getEmployeeAssignments: async (employeeId) => {
    const response = await client.get(`/assignments/employee/${employeeId}`)
    return response.data
  },

  /**
   * Assign employee to project
   * @param {object} assignmentData - Assignment data
   * @returns {Promise} Created assignment
   */
  createAssignment: async (assignmentData) => {
    const response = await client.post('/assignments/', assignmentData)
    return response.data
  },

  /**
   * Update assignment
   * @param {number} id - Assignment ID
   * @param {object} assignmentData - Updated assignment data
   * @returns {Promise} Updated assignment
   */
  updateAssignment: async (id, assignmentData) => {
    const response = await client.put(`/assignments/${id}`, assignmentData)
    return response.data
  },

  /**
   * End assignment
   * @param {number} id - Assignment ID
   * @param {object} data - End date and reason
   * @returns {Promise} Ended assignment
   */
  endAssignment: async (id, data) => {
    const response = await client.post(`/assignments/${id}/end`, data)
    return response.data
  },

  /**
   * Transfer employee to new project
   * @param {number} id - Current assignment ID
   * @param {object} transferData - Transfer details
   * @returns {Promise} Transfer result with old and new assignments
   */
  transferAssignment: async (id, transferData) => {
    const response = await client.post(`/assignments/${id}/transfer`, transferData)
    return response.data
  },

  /**
   * Terminate all employee assignments
   * @param {number} employeeId - Employee ID
   * @param {object} data - End date and reason
   * @returns {Promise} Termination summary
   */
  terminateEmployee: async (employeeId, data) => {
    const response = await client.post(`/assignments/employee/${employeeId}/terminate`, data)
    return response.data
  },

  // ==================== Excel Import ====================

  /**
   * Download the project import Excel template
   * @returns {Promise<Blob>} Excel file blob
   */
  downloadImportTemplate: async () => {
    const response = await client.get('/import/template', { responseType: 'blob' })
    return response.data
  },

  /**
   * Import projects from an Excel file
   * @param {File} file - Excel file to import
   * @returns {Promise} Import results { created, skipped, errors }
   */
  importProjects: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await client.post('/import/projects', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
}

// Exported so a contract test can install a fake axios adapter at the TRANSPORT layer and still
// exercise the real request interceptor — the same shape hr.js uses. Stubbing `projectsService`
// instead would test around the layer where a request's params get lost.
export const projectsClient = client
